import { useState, useRef, useEffect } from "react";
import DataTable from "react-data-table-component";
import CustomLoader from "../../../Components/custom/CustomLoader";
import PaginationControl from "../../../Components/custom/PaginationControl";
import {
  AiFillEdit,
  AiFillLock,
  AiFillUnlock,
  AiOutlineUnlock,
} from "react-icons/ai";
import { BsFillTrashFill, BsPlusCircleFill } from "react-icons/bs";
import { toast } from "react-toastify";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdPassword } from "react-icons/md";
import Unauthorized from "../../../Components/custom/Unauthrized";
import { useFetch, useMutate } from "../../../hooks/queries";

const UsersList = (props) => {
  const [show, setShow] = useState(false);
  const [user, setUser] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState({
    type: "",
    userId: null,
    userData: null,
    message: "",
    title: "",
  });
  const [errors, setErrors] = useState({});
  console.log("VFDfdvfd", user);
  // Refs for focusing on error fields
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const roleIdRef = useRef(null);

  const handleClose = () => {
    setShow(false);
    setIsEdit(false);
    setUser({});
    setErrors({});
  };

  const handleCloseConfirm = () => {
    setShowConfirmDialog(false);
    setConfirmAction({
      type: "",
      userId: null,
      userData: null,
      message: "",
      title: "",
    });
  };

  const handleConfirm = () => {
    switch (confirmAction.type) {
      case "lock":
        lockUserMutation.mutate(confirmAction.userId);
        break;
      case "unlock":
        unlockUserMutation.mutate(confirmAction.userId);
        break;
      case "reset":
        resetUserMutation.mutate(confirmAction.userId);
        break;
      case "delete":
        toast.warning("This feature is not supported.");
        break;
      default:
        break;
    }
    handleCloseConfirm();
  };

  const showConfirmation = (type, userId, userData, title, message) => {
    setConfirmAction({ type, userId, userData, message, title });
    setShowConfirmDialog(true);
  };

  const {
    data: usersResponse,
    isPending: usersPending,
    isLoading: usersLoading,
    isFetching: usersFetching,
    refetch: refetchUsers,
  } = useFetch({
    queryKey: ["usersList"],
    url: process.env.REACT_APP_API_URL + "Users/GetUsersList/",
    method: "GET",
    enabled: props.userPermissions.viewUsers !== false,
  });

  const users = usersResponse?.data?.content || usersResponse?.content || [];
  const pending = usersPending || usersLoading || usersFetching;

  const {
    data: rolesResponse,
    isPending: rolesPending,
    isLoading: rolesLoading,
    isFetching: rolesFetching,
  } = useFetch({
    queryKey: ["rolesList"],
    url: process.env.REACT_APP_API_URL + "Roles/GetRoles/",
    method: "GET",
    enabled: props.userPermissions.viewUsers !== false,
  });

  const roles = rolesResponse?.data?.content || rolesResponse?.content || [];
  const pendingRoles = rolesPending || rolesLoading || rolesFetching;

  const unlockUserMutation = useMutate({
    mutationKey: ["unlockUser"],
    url: (userId) =>
      process.env.REACT_APP_API_URL + "Users/UnlockUser/" + userId,
    method: "post",
    onSuccess: (data) => {
      if (data?.statusCode === 200) {
        toast.success("Unlocked");
        refetchUsers();
      }
    },
  });

  const lockUserMutation = useMutate({
    mutationKey: ["lockUser"],
    url: (userId) => process.env.REACT_APP_API_URL + "Users/LockUser/" + userId,
    method: "post",
    onSuccess: (data) => {
      if (data?.statusCode === 200) {
        toast.success("Locked");
        refetchUsers();
      }
    },
  });

  const getUserMutation = useMutate({
    mutationKey: ["getUser"],
    url: (userId) => process.env.REACT_APP_API_URL + "Users/GetUser/" + userId,
    method: "Get",
    onSuccess: (data) => {
      if (data?.statusCode === 200) {
        setUser(data.content);
        setErrors({});
        setShow(true);
        setIsEdit(true);
      }
    },
  });

  const addUserMutation = useMutate({
    mutationKey: ["addUser"],
    url: process.env.REACT_APP_API_URL + "Users/AddUser/",
    method: "Post",
    onSuccess: (data) => {
      if (data?.statusCode === 200) {
        refetchUsers();
        setUser({});
        setErrors({});
        handleClose();
        toast.success("User added successfully");
      } else {
        toast.error(data?.message);
      }
    },
    onError: (error) => {
      if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        setErrors(validationErrors);
        focusFirstErrorField(validationErrors);
        toast.warning("Please review and correct the required fields");
      } else {
        toast.error(error?.response?.data?.message || "Failed to add user");
      }
    },
  });

  const updateUserMutation = useMutate({
    mutationKey: ["updateUser"],
    url: (payload) =>
      process.env.REACT_APP_API_URL + "Users/UpdateUser/" + payload.userId,
    method: "Patch",
    onSuccess: (data) => {
      if (data?.statusCode === 200) {
        refetchUsers();
        setUser({});
        setErrors({});
        handleClose();
        toast.success("User updated successfully");
      }
    },
    onError: (error) => {
      if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        setErrors(validationErrors);
        focusFirstErrorField(validationErrors);
        toast.warning("Please review and correct the required fields");
      } else {
        toast.error(error?.response?.data?.message || "Failed to update user");
      }
    },
  });

  const resetUserMutation = useMutate({
    mutationKey: ["resetUserPassword"],
    url: (userId) =>
      process.env.REACT_APP_API_URL + "Users/ResetUserPassword/" + userId,
    method: "patch",
    onSuccess: (data) => {
      if (data?.statusCode === 200) {
        toast.success("Reset Email Sent");
      }
    },
  });

  const unlockUser = (userId, userData) => {
    showConfirmation(
      "unlock",
      userId,
      userData,
      "Unlock User",
      `Are you sure you want to unlock this user (${userData.firstName} ${userData.lastName})?`,
    );
  };

  const lockUser = (userId, userData) => {
    showConfirmation(
      "lock",
      userId,
      userData,
      "Lock User",
      `Are you sure you want to lock this user (${userData.firstName} ${userData.lastName})?`,
    );
  };

  const getUser = (userId) => {
    getUserMutation.mutate(userId);
  };

  const AddUser = () => {
    // Validate required fields before submission
    const validationErrors = {};
    let hasErrors = false;

    if (!user.firstName || user.firstName.trim() === "") {
      validationErrors.FirstName = ["The FirstName field is required."];
      hasErrors = true;
    }

    if (!user.lastName || user.lastName.trim() === "") {
      validationErrors.LastName = ["The LastName field is required."];
      hasErrors = true;
    }

    if (!user.email || user.email.trim() === "") {
      validationErrors.Email = ["The Email field is required."];
      hasErrors = true;
    }

    if (!user.roleId || user.roleId === 0) {
      validationErrors.RoleId = ["The Role field is required."];
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(validationErrors);
      focusFirstErrorField(validationErrors);
      toast.warning("Please review and correct the required fields");
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    if (isEdit) {
      updateUserMutation.mutate({ ...user, roleId: user.roleId.toString() });
    } else {
      addUserMutation.mutate({ ...user, roleId: user.roleId.toString() });
    }
  };

  const ResetUser = (userData) => {
    if (!userData?.userId) return;
    showConfirmation(
      "reset",
      userData.userId,
      userData,
      "Reset User Password",
      `Are you sure you want to reset the password for ${userData.firstName} ${userData.lastName}? A password reset email will be sent to ${userData.email}.`,
    );
  };

  const handleEdit = (userId) => {
    getUser(userId);
    setIsEdit(true);
  };

  const deleteUser = (userData) => {
    // Get current logged-in user ID
    const currentUserId = localStorage.getItem("uTsReD");

    // Check if user is trying to delete themselves
    if (
      currentUserId &&
      userData.userId &&
      parseInt(currentUserId) === parseInt(userData.userId)
    ) {
      toast.info(
        "You cannot delete your own account. Please contact an administrator if you need assistance with account management.",
      );
      return;
    }

    // Proceed with delete confirmation for other users
    showConfirmation(
      "delete",
      userData.userId,
      userData,
      "Delete User",
      `Are you sure you want to delete this user (${userData.firstName} ${userData.lastName})? This feature is currently not supported.`,
    );
  };

  const focusFirstErrorField = (validationErrors) => {
    // Use setTimeout to ensure DOM is updated with error messages before focusing
    setTimeout(() => {
      // Focus on the first field with an error in order: FirstName, LastName, Email, RoleId
      if (validationErrors.FirstName && firstNameRef.current) {
        firstNameRef.current.focus();
      } else if (validationErrors.LastName && lastNameRef.current) {
        lastNameRef.current.focus();
      } else if (validationErrors.Email && emailRef.current) {
        emailRef.current.focus();
      } else if (validationErrors.RoleId && roleIdRef.current) {
        roleIdRef.current.focus();
      }
    }, 100);
  };

  // Map between PascalCase (API errors) and camelCase (state)
  const fieldNameMap = {
    FirstName: "firstName",
    LastName: "lastName",
    Email: "email",
    RoleId: "roleId",
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // Calculate paginated data
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedUsers = users.slice(startIndex, endIndex);

  const handleInputChange = (apiFieldName, value) => {
    const stateFieldName =
      fieldNameMap[apiFieldName] || apiFieldName.toLowerCase();

    setUser((prevState) => ({
      ...prevState,
      [stateFieldName]: value,
    }));

    // Clear error for this field when user starts typing (using API field name for errors)
    if (errors[apiFieldName]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[apiFieldName];
        return newErrors;
      });
    }
  };

  const columns = [
    {
      name: "#",
      selector: (row) => row.userId,
      width: "90px",
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.firstName + " " + row.lastName,
      sortable: true,
      width: "250px",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      width: "300px",
    },
    {
      name: "Role",
      selector: (row) => row.roleName,
      sortable: true,
      width: "250px",
    },
    {
      name: "Active",
      selector: (row) => (row.isActive == true ? "Yes" : "No"),
      sortable: true,
      width: "90px",
    },
    {
      name: "Locked",
      selector: (row) => (row.isLocked == true ? "Yes" : "No"),
      sortable: true,
    },
    {
      name: "Options",
      width: "180px",
      selector: (row) => (
        <>
          {row.isLocked == true ? (
            <OverlayTrigger
              key="unlock"
              placement="top"
              overlay={
                <Tooltip id={`tooltip-unlock-${row.userId}`}>
                  Unlock User
                </Tooltip>
              }
            >
              <button
                className="btn btn-icons btn-sm btn-danger cts-btn-danger mr-5"
                onClick={() => unlockUser(row.userId, row)}
              >
                <AiFillLock />
              </button>
            </OverlayTrigger>
          ) : (
            <OverlayTrigger
              key="lock"
              placement="top"
              overlay={
                <Tooltip id={`tooltip-lock-${row.userId}`}>Lock User</Tooltip>
              }
            >
              <button
                className="btn btn-icons btn-sm btn-primary cts-btn-primary mr-5"
                onClick={() => lockUser(row.userId, row)}
              >
                <AiFillUnlock />
              </button>
            </OverlayTrigger>
          )}
          <OverlayTrigger
            key="edit"
            placement="top"
            overlay={
              <Tooltip id={`tooltip-edit-${row.userId}`}>Edit User</Tooltip>
            }
          >
            <button
              className="btn btn-icons btn-sm btn-warning cts-btn-warning mr-5"
              onClick={() => handleEdit(row.userId)}
            >
              <AiFillEdit />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            key="reset"
            placement="top"
            overlay={
              <Tooltip id={`tooltip-reset-${row.userId}`}>
                Reset Password
              </Tooltip>
            }
          >
            <button
              className=" btn btn-icons btn-sm btn-info mr-5"
              onClick={() => ResetUser(row)}
            >
              <MdPassword />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            key="delete"
            placement="top"
            overlay={
              <Tooltip id={`tooltip-delete-${row.userId}`}>Delete User</Tooltip>
            }
          >
            <button
              className="btn btn-icons btn-sm btn-danger cts-btn-danger mr-5"
              onClick={() => deleteUser(row)}
            >
              <BsFillTrashFill />
            </button>
          </OverlayTrigger>
        </>
      ),
    },
  ];
  console.log("BFfbd");
  return (
    <>
      {props.userPermissions.viewUsers == false ? (
        <Unauthorized />
      ) : (
        <div className="container-fluid mt-30">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Users List</h3>
                  <div className="align-self-center">
                    <button
                      className="btn btn-sm btn-primary cts-btn-primary d-flex align-items-center gap-2"
                      onClick={() => {
                        setUser({});
                        setErrors({});
                        setIsEdit(false);
                        setShow(true);
                      }}
                    >
                      <BsPlusCircleFill size={16} />
                      Add User
                    </button>
                  </div>
                </div>
                <div className="card-body p-0">
                  <DataTable
                    columns={columns}
                    data={paginatedUsers}
                    progressPending={pending}
                    progressComponent={<CustomLoader />}
                    fixedHeader={true}
                    fixedHeaderScrollHeight={"620px"}
                    defaultSortFieldId={1}
                  />
                  <PaginationControl
                    totalCount={users.length}
                    currentPage={page}
                    pageSize={perPage}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePerRowsChange}
                    pageSizeOptions={[10, 20, 50, 100, 200]}
                    disabled={pending}
                  />
                </div>
              </div>
            </div>
          </div>
          <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
              <Modal.Title>{isEdit ? "Edit " : "Add "} User</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="row mb-3">
                <div className="col-6">
                  <label>
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    ref={firstNameRef}
                    className={`form-control form-control-sm ${errors.FirstName ? "is-invalid" : ""}`}
                    placeholder="First Name"
                    value={user.firstName || ""}
                    onChange={(e) =>
                      handleInputChange("FirstName", e.target.value)
                    }
                  ></input>
                  {errors.FirstName && (
                    <div
                      className="text-warning"
                      style={{ fontSize: "0.875rem" }}
                    >
                      {errors.FirstName[0]}
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <label>
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    ref={lastNameRef}
                    className={`form-control form-control-sm ${errors.LastName ? "is-invalid" : ""}`}
                    placeholder="Last Name"
                    value={user.lastName || ""}
                    onChange={(e) =>
                      handleInputChange("LastName", e.target.value)
                    }
                  ></input>
                  {errors.LastName && (
                    <div
                      className="text-warning"
                      style={{ fontSize: "0.875rem" }}
                    >
                      {errors.LastName[0]}
                    </div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <label>
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    ref={emailRef}
                    className={`form-control form-control-sm ${errors.Email ? "is-invalid" : ""}`}
                    placeholder="Email"
                    value={user.email || ""}
                    onChange={(e) => handleInputChange("Email", e.target.value)}
                  ></input>
                  {errors.Email && (
                    <div
                      className="text-warning"
                      style={{ fontSize: "0.875rem" }}
                    >
                      {errors.Email[0]}
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <label>
                    Role <span className="text-danger">*</span>
                  </label>
                  <select
                    ref={roleIdRef}
                    className={`form-control form-control-sm ${errors.RoleId ? "is-invalid" : ""}`}
                    value={user.roleId || 0}
                    onChange={(e) => {
                      const value =
                        e.target.value === "0" ? 0 : parseInt(e.target.value);
                      handleInputChange("RoleId", value);
                    }}
                  >
                    <option value={0}>-- Select Role --</option>
                    {roles.map((o) => (
                      <option key={o.roleId} value={o.roleId}>
                        {o.roleName}
                      </option>
                    ))}
                  </select>
                  {errors.RoleId && (
                    <div
                      className="text-warning"
                      style={{ fontSize: "0.875rem" }}
                    >
                      {errors.RoleId[0]}
                    </div>
                  )}
                  {pendingRoles && (
                    <small className="text-muted">Loading roles...</small>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleClose}
                className="btn btn-primary btn-sm cts-btn-secondary"
              >
                Close
              </Button>
              <button
                variant="primary"
                onClick={() => AddUser()}
                className="btn btn-primary btn-sm cts-btn-primary"
              >
                Save Changes
              </button>
            </Modal.Footer>
          </Modal>

          {/* Confirmation Dialog */}
          <Modal show={showConfirmDialog} onHide={handleCloseConfirm} centered>
            <Modal.Header closeButton>
              <Modal.Title>{confirmAction.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>{confirmAction.message}</p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleCloseConfirm}
                className="btn btn-sm cts-btn-secondary"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                className="btn btn-sm cts-btn-primary"
              >
                Confirm
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </>
  );
};

export default UsersList;
